import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, MoreVertical, RotateCcw, Table2 } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import './TableAdvanced.css';

/*
  SEPARADORES STICKY — SOLUCIÓN DEFINITIVA
  ─────────────────────────────────────────
  Ver TableAdvanced.css para clases sticky-sep-r, sticky-sep-l y dot-wave
*/

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface TableAdvancedColumn<T> {
  key: string;
  header: string | React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  className?: string;
  render?: (item: T) => React.ReactNode;
  tooltip?: (item: T) => string | undefined;
  stopClickPropagation?: boolean;
  /**
   * Ancho del bloque shimmer en estado loading.
   * String CSS ("60%") o rango [min, max] en px para variar por fila.
   * Por defecto: rango [48, 120]
   */
  skeletonWidth?: string | [number, number];
  /**
   * Permite que el contenido de la celda haga wrap (múltiples líneas).
   * Por defecto false (whitespace-nowrap).
   */
  wrap?: boolean;
  /**
   * Ancho mínimo en px que la columna nunca debe bajar, incluso con responsiveColumns activo.
   */
  minWidthPx?: number;
  /**
   * Marca esta columna como elástica: en vez de un `width` fijo, ocupa el espacio
   * sobrante de la tabla (contenedor menos el resto de columnas fijas), acotado
   * entre flexMin y flexMax. Solo debe usarse en una columna por tabla.
   */
  flexMin?: number;
  flexMax?: number;
}

export interface TableAdvancedAction<T> {
  icon: React.ReactNode | ((item: T) => React.ReactNode);
  label: string;
  onClick: (item: T) => void;
  id?: string | ((item: T) => string);
  name?: string | ((item: T) => string);
  className?: string | ((item: T) => string);
  variant?: 'default' | 'destructive';
  /** Si devuelve false, la acción no aparece en el menú para ese item. */
  visible?: (item: T) => boolean;
  tooltipSize?: 'sm' | 'md' | 'lg';
  tooltipNoWrap?: boolean;
}

export interface TableAdvancedProps<T> {
  columns: TableAdvancedColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string | number;
  visibleColumnIds?: string[];
  /** Clases adicionales para el contenedor raíz de la tabla. */
  className?: string;
  actions?: TableAdvancedAction<T>[];
  contextMenuActions?: TableAdvancedAction<T>[];
  onRowClick?: (item: T) => void;
  onRowSecondaryAction?: (item: T) => void;
  secondaryActionIcon?: React.ReactNode;
  secondaryActionTooltip?: string;
  rowDisabled?: (item: T) => boolean;
  rowClassName?: (item: T, index: number) => string;
  stickyHeader?: boolean;
  minTableWidth?: number;
  emptyState?: React.ReactNode;
  maxVisibleActions?: number;
  actionsColumnWidth?: string;
  /**
   * Número de columnas visibles (desde la izquierda) que permanecen fijas
   * al hacer scroll horizontal. Por defecto 0 (sin sticky).
   * IMPORTANTE: las columnas sticky deben tener width definido (number en px).
   */
  stickyColumns?: number;
  /**
   * Key del item actualmente seleccionado (ej: el que está abierto en un sidepanel).
   * Mantiene el fondo activo en los td, sobrescribiendo el inline background que
   * de otro modo ignoraría la clase rowClassName.
   */
  selectedKey?: string | number | null;

  // ── Density ───────────────────────────────────────────────────────────────

  /**
   * Controla el padding vertical de celdas. Por defecto: 'comfortable'.
   * No afecta a las tablas que no pasan esta prop (default reproduce el valor histórico).
   */
  density?: 'comfortable' | 'intermediate' | 'compact';

  // ── Selección masiva ──────────────────────────────────────────────────────

  /**
   * Activa la columna de checkboxes para selección masiva. Por defecto: false.
   */
  selectable?: boolean;
  /** IDs de las filas actualmente seleccionadas. */
  selectedIds?: (string | number)[];
  /** Llamado al hacer click en el checkbox de una fila. */
  onToggleSelection?: (key: string | number) => void;
  /** Llamado al hacer click en el checkbox maestro del header. Recibe todos los keys del data actual. */
  onToggleAll?: (keys: (string | number)[]) => void;

  // ── Estados de carga ──────────────────────────────────────────────────────

  /**
   * Muestra el skeleton shimmer en lugar de filas reales.
   */
  esCargando?: boolean;
  /**
   * Número de filas skeleton a renderizar. Por defecto: 8.
   */
  skeletonRows?: number;
  /**
   * Texto bajo el spinner de loading. Por defecto: "Cargando..."
   */
  loadingText?: string;

  /**
   * Muestra el estado de error con mensaje y botón de reintentar.
   * Tiene prioridad sobre `esCargando` si ambos son true.
   */
  isError?: boolean;
  /**
   * Mensaje descriptivo bajo el título de error.
   */
  errorMessage?: string;
  /**
   * Callback del botón "Reintentar". Si no se pasa, el botón no aparece.
   */
  onRetry?: () => void;
  /**
   * Nodo completamente personalizado para reemplazar el estado de error por defecto.
   */
  errorState?: React.ReactNode;
  /**
   * Escala los anchos de columna al 80% en pantallas < 1600px.
   * Útil para tablas con muchas columnas que necesitan más espacio en pantallas medianas.
   * Por defecto: false.
   */
  responsiveColumns?: boolean;
  /**
   * Fuerza table-layout: fixed sin necesidad de stickyColumns.
   * Necesario para que min-width/max-width en columnas sin `width` (ej. una
   * columna que debe repartirse el espacio sobrante dentro de un rango) se
   * respeten de forma consistente entre navegadores.
   */
  fixedLayout?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const CHECKBOX_WIDTH = 48;

// ─────────────────────────────────────────────────────────────────────────────
// Utilidades
// ─────────────────────────────────────────────────────────────────────────────

function widthToPx(width?: number | string): number {
  if (typeof width === 'number') return width;
  if (typeof width === 'string') {
    const parsed = parseInt(width, 10);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componentes internos de estado
// ─────────────────────────────────────────────────────────────────────────────

interface StatePanelProps {
  icon: React.ReactNode;
  iconBg: string;   // clases tailwind para el fondo del icono
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const StatePanel: React.FC<StatePanelProps> = ({ icon, iconBg, title, description, action }) => (
  <div className="py-12 flex flex-col items-center gap-3 text-center">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${iconBg}`}>
      {icon}
    </div>
    <p className="text-h5 font-display text-primary">{title}</p>
    {description && (
      <p className="text-body-sm text-tertiary max-w-xs">{description}</p>
    )}
    {action}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export const TableAdvanced = <T,>({
  columns,
  data,
  keyExtractor,
  visibleColumnIds,
  className = '',
  actions = [],
  contextMenuActions = [],
  onRowClick,
  onRowSecondaryAction,
  secondaryActionIcon,
  secondaryActionTooltip = 'Ver detalle',
  rowDisabled,
  rowClassName,
  stickyHeader = true,
  minTableWidth = 1200,
  emptyState,
  maxVisibleActions = 3,
  actionsColumnWidth,
  stickyColumns = 0,
  selectedKey = null,
  density = 'comfortable',
  selectable = false,
  selectedIds,
  onToggleSelection,
  onToggleAll,
  // loading
  esCargando = false,
  loadingText = 'Cargando...',
  // error
  isError = false,
  errorMessage = 'Ocurrió un error al obtener los datos. Revisa tu conexión e intenta de nuevo.',
  onRetry,
  errorState,
  responsiveColumns = false,
  fixedLayout = false,
}: TableAdvancedProps<T>) => {
  const [menuOpenId, setMenuOpenId] = useState<string | number | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number } | null>(null);
  const [hoveredRowKey, setHoveredRowKey] = useState<string | number | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Ancho real del contenedor scrolleable, usado para calcular la columna elástica (flexMin/flexMax)
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Escala de columnas: 80% en pantallas < 1600px cuando responsiveColumns=true
  const [colScale, setColScale] = useState(() =>
    responsiveColumns && typeof window !== 'undefined' && window.innerWidth < 1600 ? 0.8 : 1
  );
  useEffect(() => {
    if (!responsiveColumns) return;
    const update = () => setColScale(window.innerWidth < 1600 ? 0.8 : 1);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [responsiveColumns]);

  const scalePx = (px: number) => Math.round(px * colScale);

  const visibleColumns = visibleColumnIds
    ? columns.filter(col => visibleColumnIds.includes(col.key))
    : columns;

  // Offset base para columnas sticky cuando hay checkbox de selección
  const stickyBaseOffset = selectable ? CHECKBOX_WIDTH : 0;

  const stickyLeftOffsets = visibleColumns.map((_, idx) => {
    const raw = idx === 0
      ? stickyBaseOffset
      : stickyBaseOffset + visibleColumns.slice(0, idx).reduce((acc, col) => acc + widthToPx(col.width), 0);
    return scalePx(raw);
  });

  const isStickyLeft = (colIndex: number) => colIndex < stickyColumns;
  const isLastSticky = (colIndex: number) => colIndex === stickyColumns - 1;

  const getStickyLeftStyle = (colIndex: number): React.CSSProperties => ({
    position: 'sticky',
    left: stickyLeftOffsets[colIndex],
    zIndex: 2,
  });

  const activeItem = data.find(item => keyExtractor(item) === menuOpenId);

  const closeMenu = useCallback(() => {
    setMenuOpenId(null);
    setMenuCoords(null);
  }, []);

  const getAlignClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center': return 'text-center';
      case 'right':  return 'text-right';
      default:       return 'text-left';
    }
  };

  const handleContextMenuClick = (item: T, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const itemId = keyExtractor(item);
    if (menuOpenId === itemId) { closeMenu(); return; }

    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_WIDTH = 208;
    const margin = 16;
    const scrollX = window.scrollX ?? window.pageXOffset;
    const scrollY = window.scrollY ?? window.pageYOffset;

    const visibleActions = contextMenuActions.filter(a => !a.visible || a.visible(item));
    const MENU_HEIGHT = visibleActions.length * 36 + 8;

    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < MENU_HEIGHT + margin;

    const top = openUpward
      ? Math.max(scrollY + 8, scrollY + rect.bottom - MENU_HEIGHT - 4)
      : scrollY + rect.bottom + 6;

    const minLeft = scrollX + margin;
    const maxLeft = Math.max(minLeft, scrollX + window.innerWidth - MENU_WIDTH - margin);
    const desiredLeft = scrollX + rect.right - MENU_WIDTH;
    const left = Math.min(Math.max(desiredLeft, minLeft), maxLeft);

    setMenuCoords({ top, left });
    setMenuOpenId(itemId);
  };

  useEffect(() => {
    if (menuOpenId === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId, closeMenu]);

  useEffect(() => {
    if (menuOpenId === null) return;
    const handleScroll = () => closeMenu();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [menuOpenId, closeMenu]);

  const portalTarget = typeof window !== 'undefined' ? document.body : null;

  const calculateActionsWidth = () => {
    if (actionsColumnWidth) return actionsColumnWidth;
    const baseActions = actions.length + (onRowSecondaryAction ? 1 : 0);
    const hasContextMenu = contextMenuActions.length > 0;
    const visibleCount = Math.min(maxVisibleActions, baseActions);
    return `${(visibleCount * 40) + (hasContextMenu ? 40 : 0)}px`;
  };

  const actionsWidth = calculateActionsWidth();
  const hasActions = actions.length > 0 || contextMenuActions.length > 0 || onRowSecondaryAction;

  // Columna elástica: absorbe el espacio sobrante del contenedor, acotada entre flexMin y flexMax
  const flexColumn = visibleColumns.find(c => c.flexMin != null || c.flexMax != null);
  const flexWidth = (() => {
    if (!flexColumn) return undefined;
    const othersWidth = visibleColumns
      .filter(c => c !== flexColumn)
      .reduce((sum, c) => sum + widthToPx(c.width), 0);
    const reserved = stickyBaseOffset + othersWidth + (hasActions ? widthToPx(actionsWidth) : 0);
    const target = Math.max(containerWidth, scalePx(minTableWidth));
    const available = target - reserved;
    const min = flexColumn.flexMin ?? 0;
    const max = flexColumn.flexMax ?? available;
    return Math.round(Math.min(Math.max(available, min), max));
  })();

  const getColumnStyle = (column: TableAdvancedColumn<T>): React.CSSProperties | undefined => {
    if (column === flexColumn) {
      const w = flexWidth ?? column.flexMin ?? column.flexMax;
      return w ? { width: `${w}px`, minWidth: `${w}px` } : undefined;
    }
    if (!column.width) return undefined;
    const scaled = scalePx(widthToPx(column.width));
    const w = (colScale < 1 && column.minWidthPx) ? Math.max(scaled, column.minWidthPx) : scaled;
    return { width: `${w}px`, minWidth: `${w}px` };
  };

  // Padding vertical de celdas según density
  const cellPy = density === 'compact' ? 'py-1' : density === 'intermediate' ? 'py-1.5' : 'py-2.5';

  // Datos de selección masiva
  const allDataKeys = data.map(item => keyExtractor(item));
  const allSelected = data.length > 0 && allDataKeys.every(k => (selectedIds ?? []).includes(k));
  const someSelected = allDataKeys.some(k => (selectedIds ?? []).includes(k));

  const contextMenu = portalTarget && menuOpenId !== null && menuCoords && activeItem
    ? createPortal(
        <div className="fixed inset-0 z-[1000] pointer-events-none">
          <div
            ref={menuRef}
            className="pointer-events-auto rounded-lg border border-default bg-surface-0 py-1 shadow-2xl"
            style={{ position: 'absolute', top: menuCoords.top, left: menuCoords.left, width: '208px' }}
          >
            {contextMenuActions
              .filter(action => !action.visible || action.visible(activeItem))
              .map((action, index) => {
                const iconContent = typeof action.icon === 'function' ? action.icon(activeItem) : action.icon;
                const customClass = action.className
                  ? (typeof action.className === 'function' ? action.className(activeItem) : action.className)
                  : '';
                const actionId = typeof action.id === 'function' ? action.id(activeItem) : action.id;
                const actionName = typeof action.name === 'function' ? action.name(activeItem) : action.name;
                const baseClass = action.variant === 'destructive'
                  ? 'w-full px-3 py-2 text-left text-xs font-semibold text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                  : 'w-full px-3 py-2 text-left text-xs font-medium text-secondary transition hover:bg-surface-hover';
                return (
                  <button
                    key={index}
                    id={actionId}
                    name={actionName}
                    className={customClass || baseClass}
                    onClick={(e) => { e.stopPropagation(); action.onClick(activeItem); closeMenu(); }}
                  >
                    <div className="flex items-center gap-2">
                      {iconContent && <span className="w-4 h-4">{iconContent}</span>}
                      {action.label}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>,
        portalTarget
      )
    : null;

  // ── Thead compartido por todos los estados ───────────────────────────────
  const thead = (
    <thead className={`bg-surface-1 border-b border-default ${stickyHeader ? 'sticky top-0 z-20' : ''}`}>
      <tr>
        {/* Columna checkbox — sólo cuando selectable=true */}
        {selectable && (
          <th
            className={`px-3 ${cellPy} sticky left-0 z-[22] bg-surface-1`}
            style={{ width: scalePx(CHECKBOX_WIDTH), minWidth: scalePx(CHECKBOX_WIDTH) }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (!el) return;
                el.indeterminate = someSelected && !allSelected;
              }}
              onChange={() => onToggleAll?.(allDataKeys)}
              aria-label="Seleccionar todos"
              className="rounded border-default accent-blue-600 cursor-pointer"
            />
          </th>
        )}

        {visibleColumns.map((column, colIndex) => {
          const sticky = isStickyLeft(colIndex);
          const lastSticky = isLastSticky(colIndex);
          return (
            <th
              key={column.key}
              className={[
                `px-4 ${cellPy} text-caption-medium font-sans text-tertiary whitespace-nowrap`,
                getAlignClass(column.align),
                column.className || '',
                sticky ? 'bg-surface-1' : '',
                lastSticky ? 'sticky-sep-r' : '',
              ].join(' ')}
              style={{
                ...getColumnStyle(column),
                ...(sticky ? {
                  ...getStickyLeftStyle(colIndex),
                  zIndex: lastSticky ? 22 : 21,
                } : {}),
              }}
            >
              {column.header}
            </th>
          );
        })}

        {hasActions && (
          <th
            className={`px-4 ${cellPy} text-center text-caption-medium font-sans text-tertiary whitespace-nowrap sticky right-0 bg-surface-1 z-10 sticky-sep-l`}
            style={{ width: actionsWidth, minWidth: actionsWidth }}
          >
            Acciones
          </th>
        )}
      </tr>
    </thead>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: ERROR (prioridad máxima)
  // ────────────────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <>
        <div className="border border-default rounded-lg overflow-hidden bg-surface-0 flex flex-col h-full">
          <div className="overflow-auto relative flex-1 min-h-0">
            <table className="ta-table w-full border-collapse text-ui-sm" style={{ minWidth: `${scalePx(minTableWidth)}px` }}>
              {thead}
            </table>
          </div>

          {errorState ?? (
            <StatePanel
              iconBg="bg-red-100 dark:bg-red-900/30 text-red-500"
              icon={<AlertCircle className="w-5 h-5" />}
              title="No se pudo cargar la información"
              description={errorMessage}
              action={
                onRetry ? (
                  <button
                    onClick={onRetry}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-default bg-surface-0 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-surface-hover"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reintentar
                  </button>
                ) : undefined
              }
            />
          )}
        </div>
        {contextMenu}
      </>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: SKELETON LOADING
  // ────────────────────────────────────────────────────────────────────────
  if (esCargando) {
    return (
      <>
        <div className="relative border border-default rounded-lg overflow-hidden bg-surface-0 flex flex-col h-full">
          <div className="overflow-auto relative flex-1 min-h-0">
            <table className="ta-table w-full border-collapse text-ui-sm" style={{ minWidth: `${scalePx(minTableWidth)}px` }}>
              {thead}
            </table>
          </div>
          {/* Spinner centrado sobre toda el área, incluyendo thead */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="flex gap-1.5 items-center">
              {([0, 0.18, 0.36] as number[]).map((delay, i) => (
                <span
                  key={i}
                  className="w-2.5 h-2.5 rounded-full bg-blue-500"
                  style={{
                    animation: 'dot-wave 1.1s ease-in-out infinite',
                    animationDelay: `${delay}s`,
                  }}
                />
              ))}
            </div>
            <span className="text-xs text-tertiary">{loadingText}</span>
          </div>
        </div>
        {contextMenu}
      </>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // RENDER: NORMAL (datos o empty)
  // ────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={`border border-default rounded-lg overflow-hidden bg-surface-0 flex flex-col ${data.length > 0 ? 'h-full' : ''} ${className}`}>
        {/* position:relative ancla sticky right:0 de Acciones a este contenedor */}
        <div ref={containerRef} className={`overflow-auto relative ${data.length > 0 ? 'flex-1 min-h-0' : ''}`}>
          <table
            className="ta-table w-full border-collapse text-ui-sm"
            style={{ minWidth: `${scalePx(minTableWidth)}px`, ...(stickyColumns > 0 || fixedLayout ? { tableLayout: 'fixed' } : {}) }}
          >
            {thead}

            <tbody>
              {data.map((item, rowIndex) => {
                const itemKey = keyExtractor(item);
                const isDisabled = rowDisabled ? rowDisabled(item) : false;
                const customRowClass = rowClassName ? rowClassName(item, rowIndex) : '';
                const isLastRow = rowIndex === data.length - 1;
                const isHovered = hoveredRowKey === itemKey;
                const isSelected = selectable
                  ? (selectedIds ?? []).includes(itemKey)
                  : (selectedKey !== null && selectedKey !== undefined && itemKey === selectedKey);
                const bgColor = isHovered
                  ? 'var(--surface-hover)'
                  : isSelected
                    ? 'var(--surface-selected)'
                    : 'var(--surface-0)';

                return (
                  <tr
                    key={itemKey}
                    className={[
                      !isLastRow ? 'border-b border-muted' : '',
                      onRowClick ? 'cursor-pointer' : '',
                      isDisabled ? 'opacity-50' : '',
                      'transition-colors',
                      customRowClass,
                    ].join(' ')}
                    onMouseEnter={() => setHoveredRowKey(itemKey)}
                    onMouseLeave={() => setHoveredRowKey(null)}
                    onClick={() => onRowClick && !isDisabled && onRowClick(item)}
                  >
                    {/* Celda checkbox — sólo cuando selectable=true */}
                    {selectable && (
                      <td
                        className={`px-3 ${cellPy} sticky left-0 z-[1]`}
                        style={{ width: CHECKBOX_WIDTH, minWidth: CHECKBOX_WIDTH, background: bgColor }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={(selectedIds ?? []).includes(itemKey)}
                          onChange={() => onToggleSelection?.(itemKey)}
                          aria-label="Seleccionar fila"
                          className="rounded border-default accent-blue-600 cursor-pointer"
                        />
                      </td>
                    )}

                    {visibleColumns.map((column, colIndex) => {
                      const cellContent = column.render
                        ? column.render(item)
                        : (item as Record<string, unknown>)[column.key];
                      const tooltip = column.tooltip ? column.tooltip(item) : undefined;
                      const sticky = isStickyLeft(colIndex);
                      const lastSticky = isLastSticky(colIndex);

                      return (
                        <td
                          key={`${itemKey}-${column.key}`}
                          className={[
                            `${column.wrap ? '' : 'whitespace-nowrap'} px-4 ${cellPy} text-ui-sm font-sans text-primary align-middle`,
                            getAlignClass(column.align),
                            column.className || '',
                            lastSticky ? 'sticky-sep-r' : '',
                          ].join(' ')}
                          style={{
                            ...getColumnStyle(column),
                            background: bgColor,
                            ...(sticky ? getStickyLeftStyle(colIndex) : {}),
                          }}
                          title={tooltip}
                          onClick={column.stopClickPropagation ? (e) => e.stopPropagation() : undefined}
                        >
                          {cellContent as React.ReactNode}
                        </td>
                      );
                    })}

                    {hasActions && (
                      <td
                        className={`whitespace-nowrap px-4 ${cellPy} font-sans text-primary align-middle text-center sticky right-0 z-[1] sticky-sep-l`}
                        style={{ background: bgColor }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="inline-flex gap-2 justify-center items-center">
                          {onRowSecondaryAction && (
                            <Tooltip text={secondaryActionTooltip} position="left">
                              <button
                                className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 text-tertiary hover:bg-surface-1 hover:text-secondary"
                                onClick={(e) => { e.stopPropagation(); onRowSecondaryAction(item); }}
                              >
                                {secondaryActionIcon}
                              </button>
                            </Tooltip>
                          )}

                          {actions.filter(action => !action.visible || action.visible(item)).slice(0, maxVisibleActions).map((action, actionIndex) => {
                            const iconContent = typeof action.icon === 'function' ? action.icon(item) : action.icon;
                            const actionId = typeof action.id === 'function' ? action.id(item) : action.id;
                            const actionName = typeof action.name === 'function' ? action.name(item) : action.name;
                            const customClass = action.className
                              ? (typeof action.className === 'function' ? action.className(item) : action.className)
                              : '';
                            const baseClass = customClass || 'inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 text-tertiary hover:bg-surface-1 hover:text-secondary';
                            return (
                              <Tooltip key={actionIndex} text={action.label} position="left" size={action.tooltipSize} noWrap={action.tooltipNoWrap}>
                                <button
                                  id={actionId}
                                  name={actionName}
                                  className={baseClass}
                                  onClick={(e) => { e.stopPropagation(); action.onClick(item); }}
                                >
                                  {iconContent}
                                </button>
                              </Tooltip>
                            );
                          })}

                          {contextMenuActions.length > 0 && (
                            <Tooltip text="Más opciones" position="left" noWrap>
                              <button
                                className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 text-tertiary hover:bg-surface-1 hover:text-secondary"
                                onClick={(e) => handleContextMenuClick(item, e)}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.length === 0 && (
            emptyState ?? (
              <StatePanel
                iconBg="bg-surface-2 text-tertiary"
                icon={<Table2 size={20} />}
                title="No hay datos para mostrar"
              />
            )
          )}
        </div>
      </div>

      {contextMenu}
    </>
  );
};

TableAdvanced.displayName = 'TableAdvanced';


